import { useEffect, useState } from 'react';
import {
  DEFAULT_API_CALLS_APP_POINT,
  DEFAULT_HOD_API_CALLS_POINT,
  DEFAULT_SITES_APP_POINT,
  EST_BASE_APP_POINTS,
  STD_BASE_APP_POINTS,
  YEARLY,
} from '../constants/spread';
import { getListPrice } from 'src/common/hooks/widgets/fetchData';
import { useCurrencyWithSymbol } from 'src/common/hooks/widgets/useCurrencyWithSymbol';

/**
 * Calculate the estimated price
 *
 * @param {object} intl used for the formatting
 * @param {string} localCurrency the language code to get the currency sign for
 * @param {number} employees : Number of employees/workstations
 * @param {boolean} breach : Includes Breach
 * @returns {Array}
 */

export interface PropsFunc {
  currency: string;
  localeCode: string;
  sitesAmount: number;
  apiCallsAmount: number;
  hodApiCallsAmount: number;
  tileValueSelected: string;
  country: string;
  setTotalAppPoint: React.Dispatch<React.SetStateAction<number>>;
}

export interface IFetchError {
  status: number;
  statusText: string;
}

export interface priceProps {
  partNumber: string;
  pricingIndicator: string;
  pricingModel: number;
  scaleQtyPrice: [
    {
      qty: number;
      price: number;
    },
  ];
}

const partsToQuery = [
  {
    partNumber: 'D042PZX',
  },
];

type MarketPlaceRequest = {
  currency: string;
  countryCode3: string;
  parts: typeof partsToQuery;
};

export const usePrice = ({
  currency,
  localeCode,
  sitesAmount,
  apiCallsAmount,
  hodApiCallsAmount,
  tileValueSelected,
  country,
  setTotalAppPoint,
}: PropsFunc) => {
  const [totalCost, setTotalCost] = useCurrencyWithSymbol(
    currency,
    localeCode,
    0,
  );
  const [totalCostMonthly, setTotalCostMonthly] = useCurrencyWithSymbol(
    currency,
    localeCode,
    0,
  );
  const [appPoints, setAppPoints] = useState(0);
  const [dataFetched, setDataFetched] = useState([]);
  const [fetchingError, setFetchingError] = useState({});
  const [loading, setLoading] = useState(false);
  const sitesPoint = sitesAmount - DEFAULT_SITES_APP_POINT;
  const apiCallsPoint = Math.round(
    apiCallsAmount / 20000 - DEFAULT_API_CALLS_APP_POINT,
  );

  const hodApiCallsPoint = Math.round(
    hodApiCallsAmount / 10000 - DEFAULT_HOD_API_CALLS_POINT,
  );

  const calculatedPrice = () => {
    let calcPrice;
    if (tileValueSelected === 'essentials') {
      calcPrice =
        EST_BASE_APP_POINTS * appPoints +
        sitesPoint * appPoints +
        apiCallsPoint * appPoints +
        hodApiCallsPoint * appPoints;
    } else {
      calcPrice =
        STD_BASE_APP_POINTS * appPoints +
        sitesPoint +
        apiCallsPoint +
        hodApiCallsPoint;
    }
    return calcPrice;
  };

  const setPricesData = (products: priceProps[]) => {
    if (products?.length) {
      const priceArray = products.map((item) => ({
        [item.partNumber]: item.scaleQtyPrice[0].price,
      }));
      const priceObj = Object.assign({}, ...priceArray);
      setAppPoints(priceObj.D042PZX);
    }
  };

  const fetchPrices = () => {
    setLoading(true);
    const body: MarketPlaceRequest = {
      currency: currency,
      countryCode3: country,
      parts: partsToQuery,
    };
    getListPrice(
      `https://api.marketplace.ibm.com/purchase/catalog/resources/price/info`,
      JSON.stringify(body),
      'POST',
    )
      .then((data) => {
        if (data.parts && data.parts.length !== 0) {
          setLoading(false);
          setPricesData(data.parts);
          setDataFetched(data.parts);
        } else {
          setLoading(false);
          setDataFetched([]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setDataFetched([]);
        if (error.response) {
          setFetchingError(error.response);
        }
      });
  };

  const price = calculatedPrice();

  useEffect(() => {
    if (currency && dataFetched.length === 0) {
      fetchPrices();
    }
  }, [currency]);

  useEffect(() => {
    setTotalCost(price * YEARLY);
    setTotalCostMonthly(price);
    setTotalAppPoint(Math.round(price / appPoints));
  }, [
    sitesAmount,
    apiCallsAmount,
    hodApiCallsAmount,
    tileValueSelected,
    appPoints,
  ]);

  return [totalCost, totalCostMonthly, loading, fetchingError];
};
